import Box from '@mui/material/Box';
import { type JSX, useState } from 'react';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { UnsavedChangesWarning } from 'Components/Text/UnsavedChangesWarning';
import { ICON_DELETE_REMOVE, ICON_SAVE } from 'Config/Icons';
import { UCRM_BUSINESS_MANAGER, UCRM_BUSINESS_NAME } from 'Config/Tooltips';
import { UCRM_ROLES } from 'Helpers/UserUtils';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { ValidationError } from 'Models/ErrorTypes';
import { FinalizationState } from 'Models/Organization';
import { GroupOptionType } from 'Models/Types/GlobalType';
import { BusinessForNonUcrmUser, BusinessResponse, CreateBusinessRequest, UpdateBusinessRequest } from 'Models/Ucrm';
import { UserResponse } from 'Models/User';

import styles from './ManageBusinessFormFields.module.css';
import { DeleteBusinessModal } from '../DeleteBusinessModal/DeleteBusinessModal';

/**
 * The props for `ManageBusinessFormFields`.
 * @param hideDeleteButton If `true`, the user will not be able to delete the business. At the time of writing, this is `true` only for the UCRM Intake page.
 * @param name If present, the text field for entering the business's name will not be rendered by this component, and it will be the parent's job to manage the UI and state for the name. At the time of writing, this is the case only for the UCRM Intake page, where the user must first choose whether they are editing a new or existing business.
 * @param business The existing business to update or delete. If omitted, the form creates a new business.
 */
export interface ManageBusinessFormFieldsProps {
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    onFormSubmitBegin: () => void;
    onFormSubmitFailed: (errorMessage: string) => void;
    onFormSubmittedSuccessfully: (business: BusinessResponse | BusinessForNonUcrmUser) => Promise<void>;
    ucrmApi: ClientUcrmApi;
    userHasFullUcrmAccess: boolean;

    hideDeleteButton?: boolean;
    name?: string;
    business?: BusinessResponse | BusinessForNonUcrmUser;
}

interface FormFieldsState {
    businessManagerUserId?: string;
    tags: string[];
}

type RequestActionNoConfirmationNeeded = 'edit' | 'createAsDraft';
type RequestActionConfirmationNeeded = 'delete';

type RequestAndModalState =
    | {
          requestState: 'idle';
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'inProgress';
          action: RequestActionNoConfirmationNeeded | RequestActionConfirmationNeeded;
          deleteConfirmationModalIsActive: false;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionNoConfirmationNeeded;
          deleteConfirmationModalIsActive: false;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionConfirmationNeeded;
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'failure';
          deleteConfirmationModalIsActive: false;
      };

/**
 * Renders a form for creating a new business, or updating or deleting an existing business.
 * Used on the Manage Business page as well as on the UCRM Intake page.
 */
export const ManageBusinessFormFields = (props: ManageBusinessFormFieldsProps): JSX.Element => {
    const cachedData = useCachedData();

    const [requestAndModalState, setRequestAndModalState] = useState<RequestAndModalState>({ requestState: 'idle', deleteConfirmationModalIsActive: false });
    const [businessManager, setBusinessManager] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.business?.vendor_manager_user_id)); // Keeps track of the currently selected user in the Business Manager drop-down menu.

    // Used only if the parent is not managing the name. Do not use this state directly; use `name`.
    const [managedName, setManagedName] = useState<string | undefined>(props.business?.name);
    const name = props.name ?? managedName;

    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({
        businessManagerUserId: props.business?.vendor_manager_user_id,
        tags: props.business?.tags ?? [],
    });

    const [hasUnsavedChanges, onFormFieldValuesSaved] = useUnsavedChangesTracking(formFieldsState, false);

    const createBusiness = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'createAsDraft', deleteConfirmationModalIsActive: false });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const business = (await props.ucrmApi.createBusiness(buildRequestForNewBusiness())).data;
            await props.onFormSubmittedSuccessfully(business);
            setRequestAndModalState({ requestState: 'success', completedAction: 'createAsDraft', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    const updateBusiness = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'edit', deleteConfirmationModalIsActive: false });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const business = (await props.ucrmApi.updateBusiness(buildRequestForExistingBusiness(), props.business!.id)).data;
            await props.onFormSubmittedSuccessfully(business);
            setRequestAndModalState({ requestState: 'success', completedAction: 'edit', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    /** Build the request object for creating a new Business. */
    const buildRequestForNewBusiness = (): CreateBusinessRequest => {
        return {
            finalization_state: FinalizationState.DRAFT_IN_PROGRESS,
            name: name!,
            vendor_manager_user_id: formFieldsState.businessManagerUserId!,
            tags: formFieldsState.tags,
        };
    };

    // TODO: Remove `Undefined` from the backend and build this like any other request in the app.
    /** Build the request object for updating an existing Business. This ensures only necessary fields are included in the request to the API. */
    const buildRequestForExistingBusiness = (): UpdateBusinessRequest => {
        const request: UpdateBusinessRequest = {};
        if (props.business!.name !== name) {
            request.name = name;
        }
        if (props.business!.vendor_manager_user_id !== formFieldsState.businessManagerUserId) {
            request.vendor_manager_user_id = formFieldsState.businessManagerUserId;
        }
        if (props.business!.tags !== formFieldsState.tags) {
            request.tags = formFieldsState.tags;
        }
        return request;
    };

    /** Perform input validation. */
    const validateForm = (): void => {
        if (!name) {
            throw new ValidationError('Name is required.');
        }
        if (!formFieldsState.businessManagerUserId) {
            throw new ValidationError('Manager is required.');
        }
    };

    /** Handles error responses from the API. */
    const handleRequestError = (error: Error): void => {
        props.onFormSubmitFailed(error.message);
        setRequestAndModalState({ requestState: 'failure', deleteConfirmationModalIsActive: false });
    };

    /** Handles changes made to the Business Manager drop-down (select) field. */
    const handleSelectUserChange = (user: UserResponse | undefined, formFieldId: string): void => {
        if (user !== businessManager) {
            if (user === undefined) {
                setBusinessManager(undefined);
                setFormFieldsState({ ...formFieldsState, [formFieldId]: undefined });
            } else {
                setBusinessManager(user);
                setFormFieldsState({ ...formFieldsState, [formFieldId]: user.cognito_subject });
            }
        }
    };

    const business = props.business;
    const formDisabled = requestAndModalState.requestState === 'inProgress' || (requestAndModalState.requestState === 'success' && requestAndModalState.completedAction === 'delete');

    return (
        <>
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.deleteConfirmationModalIsActive && business && <DeleteBusinessModal exceptionsApi={props.exceptionsApi} issuesApi={props.issuesApi} onSuccess={async () => setRequestAndModalState({ requestState: 'success', completedAction: 'delete', deleteConfirmationModalIsActive: true })} onHideModal={() => setRequestAndModalState({ ...requestAndModalState, deleteConfirmationModalIsActive: false })} business={props.business!} ucrmApi={props.ucrmApi} userHasFullUcrmAccess={props.userHasFullUcrmAccess} />}
            <Box component="form" noValidate>
                <fieldset disabled={formDisabled}>
                    {!props.name && (
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={name || ''} formFieldType="text" handleChange={(event: React.FormEvent<HTMLInputElement>) => setManagedName(event.currentTarget.value)} formFieldId="businessName" formFieldLabel="Legal Name" required tooltip={UCRM_BUSINESS_NAME} />
                        </div>
                    )}
                    <div className={styles.formFieldContainer}>
                        <FormFieldUserSelect rolesRestriction={UCRM_ROLES} selectedUser={businessManager} onUserSelected={handleSelectUserChange} formFieldId="businessManagerUserId" formFieldLabel="Manager" tooltip={UCRM_BUSINESS_MANAGER} required disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldMultiOptionSelect
                            defaultSelectedOptions={cachedData
                                .getTagCategoriesGroupedOptions()
                                .map((group) => group.options)
                                .flat()
                                .filter((option) => formFieldsState.tags.includes(option.value as string))}
                            formFieldLabel="Tags"
                            formFieldId="tags"
                            handleChange={(value: GroupOptionType[]) => setFormFieldsState((current) => ({ ...current, tags: value.map((tag) => tag.value as string) }))}
                            options={cachedData.getTagCategoriesGroupedOptions()}
                            accessibilityLabel="Tags"
                            disabled={formDisabled}
                        />
                    </div>
                    <div className={'modalFormButtonContainer'}>
                        {props.business && !props.hideDeleteButton && (
                            <div className={styles.deleteButton}>
                                <Button fontAwesomeImage={ICON_DELETE_REMOVE} onClick={() => setRequestAndModalState({ requestState: 'idle', deleteConfirmationModalIsActive: true })} variant="danger" isLoading={false} loadingText="Deleting...">
                                    Delete
                                </Button>
                            </div>
                        )}
                        <Button
                            variant="primary"
                            fontAwesomeImage={ICON_SAVE}
                            isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === (props.business ? 'edit' : 'createAsDraft')}
                            loadingText="Saving..."
                            onClick={() => {
                                if (props.business) {
                                    updateBusiness();
                                } else {
                                    createBusiness();
                                }
                            }}
                        >
                            Save
                        </Button>
                    </div>
                    <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
                </fieldset>
            </Box>
        </>
    );
};
